import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { getAllIDVs } from '@/services/IDVService';
import { getProspectById } from '@/services/ProspectService';
import { getVerificationRequestResponse } from '@/services/TreefortService';

export const saidvQueryKeys = {
    allLists: ['SAIDVs'] as const,
    list: (lawFirmIds: number[]) => ['SAIDVs', lawFirmIds] as const,
    prospect: (prospectId: string) => ['SAIDV', prospectId] as const,
    details: (prospectId: string) => ['saidvDetails', prospectId] as const,
};

export function invalidateSAIDVList(queryClient: QueryClient) {
    return queryClient.invalidateQueries({ queryKey: saidvQueryKeys.allLists });
}

export function invalidateSAIDVProspect(queryClient: QueryClient, prospectId: string) {
    return queryClient.invalidateQueries({ queryKey: saidvQueryKeys.prospect(prospectId) });
}

export function invalidateSAIDVDetails(queryClient: QueryClient, prospectId: string) {
    return queryClient.invalidateQueries({ queryKey: saidvQueryKeys.details(prospectId) });
}

export function invalidateAfterVerificationChange(queryClient: QueryClient, prospectId?: string) {
    return Promise.all([
        ...(prospectId ? [invalidateSAIDVDetails(queryClient, prospectId)] : []),
        invalidateSAIDVList(queryClient),
    ]);
}

export function invalidateAfterProspectChange(queryClient: QueryClient, prospectId?: string) {
    return Promise.all([
        ...(prospectId ? [invalidateSAIDVProspect(queryClient, prospectId)] : []),
        invalidateSAIDVList(queryClient),
    ]);
}

export function useSAIDVs(lawFirmIds: number[]) {
    return useQuery({
        queryKey: saidvQueryKeys.list(lawFirmIds),
        queryFn: async () => {
            const response = await getAllIDVs(lawFirmIds);
            return response.data;
        },
        enabled: lawFirmIds.length > 0,
        staleTime: 5 * 60_000,
    });
}

export function useSAIDV(prospectId?: string) {
    return useQuery({
        queryKey: saidvQueryKeys.prospect(prospectId ?? ''),
        queryFn: async () => {
            if (!prospectId) {
                throw new Error('prospectId is required');
            }

            const response = await getProspectById(prospectId);
            return response.data;
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });
}

export function useSAIDVDetails(prospectId?: string) {
    return useQuery({
        queryKey: saidvQueryKeys.details(prospectId ?? ''),
        queryFn: async () => {
            if (!prospectId) {
                throw new Error('prospectId is required');
            }

            const response = await getVerificationRequestResponse(prospectId);
            return response.data;
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });
}
